import React, { useEffect } from "react";
import {
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Select,
} from "@/components/ui/select";
import { useAvailableRooms, useRoom, useRooms } from "@/hooks/useRooms";
import { useHotel } from "@/hooks/useHotel";
import { NonEditableFormField } from "../../../guests/components/form/FormFields";
import { cn } from "@/lib/utils";

const RoomField = ({
  value,
  onChange,
  error,
  isEdit = true,
  showOnlyAvailable = false,
  showInline = false,
  startDate,
  endDate,
  showCurrentRoom = false,
  currentRoomId,
  labelClassName,
  contentClassName,
  containerClassName,
  showIcon = true,
}: {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  isEdit?: boolean;
  showOnlyAvailable?: boolean;
  showInline?: boolean;
  startDate?: Date;
  endDate?: Date;
  showCurrentRoom?: boolean;
  currentRoomId?: string;
  labelClassName?: string;
  contentClassName?: string;
  containerClassName?: string;
  showIcon?: boolean;
}) => {
  const { hotelId } = useHotel();
  const safeHotelId = hotelId ?? "";
  const startISO = startDate?.toISOString() ?? "";
  const endISO = endDate?.toISOString() ?? "";

  const allRoomsQuery = useRooms(
    safeHotelId,
    1,
    10,
    undefined,
    !showOnlyAvailable && !!hotelId,
  );

  const availableRoomsQuery = useAvailableRooms(
    safeHotelId,
    startISO,
    endISO,
    showOnlyAvailable && !!hotelId && !!startDate && !!endDate,
  );

  const {
    data,
    isLoading,
    error: errorLoading,
    refetch,
  } = showOnlyAvailable ? availableRoomsQuery : allRoomsQuery;

  const { data: currentRoom } = useRoom(currentRoomId, safeHotelId);

  useEffect(() => {
    if (showOnlyAvailable && !isLoading) {
      refetch();
    }
  }, [startDate, endDate, showOnlyAvailable, isLoading, refetch]);

  if (!isEdit || showInline) {
    return (
      <NonEditableFormField
        value={currentRoom?.number.toString() || ""}
        label={"Room"}
        isCopy={false}
        labelClassName={labelClassName}
        contentClassName={contentClassName}
        containerClassName={containerClassName}
      />
    );
  }

  return (
    <div className={containerClassName}>
      <label
        className={cn(
          "block text-sm font-medium text-black mb-2",
          labelClassName,
        )}
      >
        Room
      </label>
      <Select value={value} onValueChange={onChange} disabled={isLoading}>
        <SelectTrigger
          showIcon={showIcon}
          className={cn(
            `w-full flex justify-between border-2 focus:ring-0 focus:border-black hover:border-black cursor-pointer ${
              errorLoading || error ? "border-red-500" : "border-black"
            }`,
            contentClassName,
          )}
        >
          <SelectValue
            placeholder={isLoading ? "Loading rooms..." : "Choose room"}
          />
        </SelectTrigger>
        <SelectContent>
          {showCurrentRoom && currentRoom && (
            <SelectItem key={currentRoom.id} value={currentRoom.id}>
              <div className="flex items-center gap-2">
                <span>Room {currentRoom.number}</span>
                <span className="text-gray-500">- {currentRoom.type}</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs bg-gray-100 text-grey`}
                >
                  current
                </span>
              </div>
            </SelectItem>
          )}
          {data?.data.map((room) => (
            <SelectItem key={room.id} value={room.id}>
              <div className="flex items-center gap-2">
                <span>Room {room.number}</span>
                <span className="text-gray-500">- {room.type}</span>
                <span
                  className={`px-2 py-1 rounded-full text-xs ${
                    room.status === "AVAILABLE"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {room.status}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

export default RoomField;
